import { useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { getSafeReturnPath } from '@/utils/earlySupporterFormat';
import { LoadingSpinner } from './LoadingSpinner';
import { ConnectionErrorScreen } from './ConnectionErrorScreen';

/**
 * PrivateRoute Component
 *
 * Wrapper for protected routes that require authentication
 * Redirects to /login if user is not authenticated
 */

interface PrivateRouteProps {
  children: React.ReactNode;
}

export const PrivateRoute = ({ children }: PrivateRouteProps) => {
  const { isAuthenticated, isLoading, authConnectionError, user } = useAuth();
  const location = useLocation();
  // True once this route wrapper has seen an authenticated session: a later drop to
  // unauthenticated is a logout / invalidated session, not a deep-link visit.
  const wasAuthenticatedRef = useRef(false);
  if (isAuthenticated) wasAuthenticatedRef.current = true;

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  // Transient connectivity failure during the boot-time auth check — the
  // session may still be valid, so do NOT redirect to /login. Let the user
  // retry instead of silently discarding a valid 30-day refresh cookie.
  if (authConnectionError && !isAuthenticated) {
    return <ConnectionErrorScreen />;
  }

  if (!isAuthenticated) {
    // Remember where the visitor was headed (e.g. a newsletter deep link) so login can return there
    // (only on an initial unauthenticated visit, never after logout / session loss)
    const target = `${location.pathname}${location.search}${location.hash}`;
    const from = wasAuthenticatedRef.current ? null : getSafeReturnPath(target);
    return <Navigate to="/login" replace state={from && from !== '/' ? { from } : undefined} />;
  }

  // New accounts reach the plan choice immediately after any login method or registration.
  // Keep explicit deep links available; the layout gives a persistent Shop hint there.
  if (user?.needsPlan && location.pathname === '/') return <Navigate to="/shop" replace />;

  return <>{children}</>;
};
